import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, concatMap, from, of, toArray } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../../components/modal-escape-guard/modal-escape-guard.directive';
import { TestStepService } from '../../../services/test-step.service';
import { CreateTestStepRequest, TestStep, UpdateTestStepRequest } from '../../../models/test-case.model';
import {
  SchrittKontext,
  TestStepLibraryDialogComponent,
} from '../test-step-library-dialog/test-step-library-dialog.component';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { AvatarComponent } from '../../../components/avatar/avatar.component';
import {
  DRAFT_ID,
  createDraftStep,
  createLocalModuleRefStep,
  createLocalStep,
  isDraftId,
  isLocalId,
  nextLocalId,
} from './test-step-local.util';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { valuesEqual } from '../../../shared/forms/dirty-tracker';
import { ModuleEmbedDialogComponent } from '../../module/module-embed-dialog/module-embed-dialog.component';
import { ModuleStepTreeComponent } from '../../module/module-step-tree/module-step-tree.component';
import { TestModule, TestModuleStatus } from '../../../models/test-module.model';
import { TestModuleStore } from '../../../services/test-module-store';
import { ScrollContainerDirective } from '../../../shared/scroll-container/scroll-container.directive';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';
import {
  TestdatenBlockComponent,
  TestdatenBlockRow,
  toTestdatenblattSummary,
} from '../../../components/testdaten-block/testdaten-block.component';
import { StepConditionEditorComponent } from '../../../components/step-condition-editor/step-condition-editor.component';
import { StepConditionListComponent } from '../../../components/step-condition-list/step-condition-list.component';
import { ObjectMappingService } from '../../../services/object-mapping.service';
import { ConditionLibrary } from '../../../models/object-mapping.model';
import { SecretColumnError } from '../../../shared/http/server-message';
import { TdbOverride, bedingungFragment, formatTcdlPreview, tdbIdSegment } from '../../../shared/tcdl-ops';
import { TdbOverrideDelta, withFirstStepCondition, withSavedStepConditions } from '../../../shared/tdb-override-delta';
import {
  StepCondition,
  activeRowCount,
  filterEntriesFor,
  formatFilterExpression,
  fromStepCondition,
  fromStepConditions,
  missingConditionFields,
  nextStepCondition,
  stepConditionsOf,
  toStepConditions,
} from '../../../shared/data-sheet-filters.codec';
import { TcdlEditorComponent } from '../../../components/tcdl-editor/tcdl-editor.component';
import { StepKeyPillComponent } from '../../../components/step-key-pill/step-key-pill.component';
import { KiBefundInlineComponent } from '../../../components/ki/ki-befund-inline/ki-befund-inline.component';
import { fokusImBereichHalten } from '../../../components/ki/ki-fokus';
import { KiMarkComponent } from '../../../components/ki/ki-mark/ki-mark.component';
import { KiSchrittBefund } from '../../../models/ki.model';
import { RecursiveBadgeComponent } from '../../../components/recursive-badge/recursive-badge.component';

/** Read-Mode kollabiert TCDL, sobald mehr als so viele Zeilen vorhanden sind (NEX-1047). */
const TCDL_COLLAPSE_THRESHOLD = 4;

/** So lange bleibt eine Zeile nach „Übernehmen“ hervorgehoben (page-testfaelle.jsx:938-947). */
const KI_HERVORHEBUNG_MS = 2600;

/** Letzte Übernahme eines KI-Vorschlags an einem Schritt; `alt` stellt „Rückgängig“ wieder her. */
interface KiAenderung {
  feld: 'ERWARTUNG' | 'SCHRITT';
  ziel: 'title' | 'action' | 'expectedResult';
  alt: string | null;
  neu: string;
}

export interface FlushResult {
  persisted: TestStep[];
  failed: TestStep[];
}

type FlushItem = TestStep | { failed: TestStep };

function isPersisted(item: FlushItem): item is TestStep {
  return !('failed' in item);
}

function withId(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
  return new Set(ids).add(id);
}

function withoutId(ids: ReadonlySet<string>, id: string): ReadonlySet<string> {
  const next = new Set(ids);
  next.delete(id);
  return next;
}

function isFailed(item: FlushItem): item is { failed: TestStep } {
  return 'failed' in item;
}

function drawerConditionRow(override: TdbOverride | undefined, condition: StepCondition | null): TdbOverride[] {
  if (override?.subj && missingConditionFields(override).length > 0) {
    const { subj, ref, op, val } = override;
    return [{ subj, ref, op, val }];
  }
  const row = fromStepCondition(condition);
  return row.subj ? [row] : [];
}

@Component({
  selector: 'app-test-step-table',
  imports: [
    NgTemplateOutlet,
    TranslatePipe,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    MatTooltip,
    TestStepLibraryDialogComponent,
    ModuleEmbedDialogComponent,
    ModuleStepTreeComponent,
    SectionTitleComponent,
    AvatarComponent,
    ScrollContainerDirective,
    TestdatenBlockComponent,
    StepConditionEditorComponent,
    StepConditionListComponent,
    TcdlEditorComponent,
    StepKeyPillComponent,
    KiBefundInlineComponent,
    KiMarkComponent,
    RecursiveBadgeComponent,
    Modal,
    ModalEscapeGuardDirective,
  ],
  templateUrl: './test-step-table.component.html',
  styleUrl: './test-step-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestStepTableComponent implements OnInit {
  private readonly stepService = inject(TestStepService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly moduleStore = inject(TestModuleStore);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  readonly projectId = input.required<string>();
  readonly testCaseId = input<string | null>(null);
  /** Modul-Scope-Owner (NEX-593): schließt {@link testCaseId} exklusiv aus. */
  readonly moduleId = input<string | null>(null);
  readonly kontext = input<SchrittKontext>('testfall');
  /**
   * NEX-538/NEX-644: Erlaubt das Einbinden ganzer Module als MODULE_REF-Zeile. Testfall-
   * UND Modul-Editor setzen das auf true (der Modul-Editor seit NEX-644, das separate
   * TestModuleEmbed-Konzept ist abgelöst) — unabhängig davon, ob der Owner schon
   * persistiert ist (Anlegen + Bearbeiten).
   */
  readonly allowModuleEmbed = input(false);

  /**
   * NEX-2259: IDs der Module mit `recursive: true` aus der Modulliste. Im Modul-Editor kennzeichnet
   * die Tabelle damit den Aufruf, der den Kreis schließt (siehe {@link closesCycle}).
   */
  readonly recursiveModuleIds = input<ReadonlySet<string>>(new Set());

  /**
   * Buffered-Mode für den Testfall-Bulk-Save (NEX-599): unterdrückt alle
   * Owner-Service-Calls (create/update/delete/reorder/copyFromLibrary) und
   * hält alle Mutationen lokal — der Parent puffert und persistiert später
   * per {@code saveFull}. {@link initialSteps} muss dann die Startliste liefern.
   */
  readonly bufferedMode = input(false);
  readonly initialSteps = input<TestStep[] | null>(null);

  /** NEX-607: unterdrückt jede Mutation für PROJECT_READER, unabhängig vom Owner-Formular. */
  readonly readOnly = input(false);
  /** Nur Anzeige: Ohne Modul-Schreibrecht bleibt „Bearbeiten“ sichtbar, aber gesperrt; das Backend prüft selbst. */
  readonly canEditModule = input(true);
  /**
   * `list` zeigt „Ausführen wenn“ als UND-Liste über dem Modulkopf (Testfall).
   * Der Modul-Editor bleibt bei `single`: eine Bedingung im Block wie bisher.
   */
  readonly conditionMode = input<'single' | 'list'>('single');

  /**
   * NEX-1051: projektweite Testdatenblatt-Liste — der Parent laedt sie einmal und
   * reicht sie durch. Wird nur fuer die Chip-/Block-Ansicht am MODULE_REF-Row gebraucht.
   */
  readonly availableSheets = input<readonly Testdatenblatt[]>([]);

  /**
   * NEX-1296: zeigt an gespeicherten Schritten die Nummer (`TS-035`) als kopierbare Pille.
   * Nur der Testfall-Editor schaltet das ein — dieselbe Tabelle steckt im Modul-Editor.
   */
  readonly showStepKeys = input(false);

  /** Offene Befunde des KI-Qualitätschecks; jeder steht als Kasten unter „Soll“ seines Schritts. */
  readonly kiBefunde = input<readonly KiSchrittBefund[]>([]);
  readonly testCaseAssigneeName = input<string | null>(null);
  /** „Übernehmen“ bzw. „Lassen“/„Erledigt“ an einem Kasten (Key des Befunds); den Stand führt der Check. */
  readonly kiUebernehmen = output<number>();
  readonly kiLassen = output<number>();
  protected readonly kiBefundeJeSchritt = computed(() => {
    const jeSchritt = new Map<string, KiSchrittBefund[]>();
    for (const befund of this.kiBefunde()) {
      jeSchritt.set(befund.schrittId, [...(jeSchritt.get(befund.schrittId) ?? []), befund]);
    }
    return jeSchritt;
  });
  /** Je Schritt eine Änderung pro Feld, damit jede Übernahme für sich rückgängig zu machen ist. */
  protected readonly kiAenderungen = signal<Record<string, readonly KiAenderung[]>>({});
  protected readonly kiNeuId = signal<string | null>(null);
  private kiNeuTimer: ReturnType<typeof setTimeout> | undefined;

  /** Emitted on every user step mutation so the parent form can mark itself dirty (NEX-535). */
  readonly changed = output<void>();

  /** Snapshot der aktuellen Steps nach jeder User-Mutation (NEX-599 Buffered-Mode). */
  readonly stepsChanged = output<TestStep[]>();

  /**
   * NEX-644: Sprung zum eingebundenen Modul (calledModuleId einer MODULE_REF-Zeile).
   * Der Parent navigiert selbst, damit seine Dirty-Behandlung greift — bei Modul→Modul
   * feuert der CanDeactivate-Guard nicht (gleiche Route, nur Param-Wechsel).
   */
  readonly moduleOpen = output<string>();

  /**
   * NEX-686/NEX-1052: Bearbeiten eines eingebundenen Moduls im Right-Drawer (ohne die
   * Seite zu verlassen). Zusaetzlich zur `moduleId` wird die `stepId` der ausloesenden
   * MODULE_REF-Zeile mitgegeben (null bei geschachtelten Modulen aus dem Baum) — der
   * Testfall-Editor braucht sie, um das TDB-Override im Drawer an genau diesen Aufruf
   * zu binden.
   */
  readonly editModule = output<{ moduleId: string; stepId: string | null }>();

  readonly hasTestCase = computed(() => !!this.testCaseId());
  readonly hasOwner = computed(() => !!this.testCaseId() || !!this.moduleId());
  /** Lokal-Modus: entweder explizit gebuffert (NEX-599) oder Anlegen ohne Owner (NEX-593). */
  private readonly isBuffered = computed(() => this.bufferedMode() || !this.hasOwner());

  readonly steps = signal<TestStep[]>([]);
  readonly draftStep = signal<TestStep | null>(null);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly busy = signal(false);

  readonly tableRows = computed<TestStep[]>(() => {
    const draft = this.draftStep();
    return draft ? [...this.steps(), draft] : this.steps();
  });

  readonly expandedTcdlRows = signal<ReadonlySet<string>>(new Set<string>());
  /**
   * Globaler Toggle (NEX-1047) — macht TCDL-Bloecke aller Zeilen sichtbar. Der
   * Zustand wird pro Owner (Testfall bzw. Modul) in `localStorage` gemerkt,
   * damit er beim erneuten Oeffnen wieder eingeblendet ist; bei fehlender Web-
   * Storage-Umgebung (SSR, Test-Sandbox) bleibt es rein reaktiv.
   */
  readonly tcdlAll = signal(false);
  readonly editingId = signal<string | null>(null);
  readonly editTitle = signal('');
  readonly editAction = signal('');
  readonly editExpected = signal('');
  readonly editNotes = signal('');
  readonly editTcdl = signal('');
  private editTcdlOriginal = '';
  private readonly editFieldsAtOpen = signal(this.currentEditFields());

  readonly rowOpen = computed(() => this.editingId() !== null);
  /** Erst nach einem Speicherversuch steht unter der offenen Zeile, warum nicht gespeichert wurde. */
  protected readonly offeneZeileGemeldet = signal(false);
  readonly openRowChanged = computed(
    () => this.rowOpen() && !valuesEqual(this.currentEditFields(), this.editFieldsAtOpen()),
  );

  /**
   * Pflichtfelder wie in der Standalone-Anlage (TestStepFormComponent: title + expectedResult
   * required); zusätzlich sperrt ein Titel über FIELD_LENGTHS.NAME_MAX Zeichen.
   */
  readonly canSaveEdit = computed(() => {
    const title = this.editTitle().trim();
    return !!title && title.length <= FIELD_LENGTHS.NAME_MAX && !!this.editExpected().trim();
  });

  private readonly editAttempted = signal(false);
  readonly titleMissing = computed(() => this.editAttempted() && !this.editTitle().trim());
  readonly expectedMissing = computed(() => this.editAttempted() && !this.editExpected().trim());
  readonly requiredMissing = computed(() => this.titleMissing() || this.expectedMissing());

  readonly pendingSwitch = signal<{ step: TestStep; tcdl: boolean } | null>(null);

  readonly fieldLengths = FIELD_LENGTHS;

  private localIdCounter = 0;

  readonly showLibrary = signal(false);

  /** NEX-538: Modul-Einbinden-Dialog — nur im Testfall-Kontext ({@link hasTestCase}). */
  readonly showModuleEmbed = signal(false);

  /** IDs der bereits als MODULE_REF-Zeile eingebundenen Module → dedupe. */
  readonly embeddedModuleIds = computed(() =>
    this.steps()
      .filter((s) => s.kind === 'MODULE_REF')
      .map((s) => s.calledModuleId)
      .filter((id): id is string => !!id),
  );

  /**
   * Auswahl im Modul-Einbinden-Dialog: ohne bereits eingebundene Module. Das Owner-Modul bleibt
   * wählbar, ein Selbstaufruf ist erlaubt (NEX-2259, löst NEX-644 ab).
   */
  readonly moduleEmbedExcludeIds = computed(() => this.embeddedModuleIds());

  /**
   * Schließt dieser Modulaufruf im Modul-Editor einen Kreis? Selbstaufruf, oder Owner und
   * aufgerufenes Modul sind beide rekursiv — dann liegen sie auf demselben Kreis (Näherung,
   * ohne eigenes Backend-Feld; NEX-2259).
   */
  closesCycle(step: TestStep): boolean {
    const owner = this.moduleId();
    const called = step.calledModuleId;
    if (!owner || !called || step.kind !== 'MODULE_REF') return false;
    const recursive = this.recursiveModuleIds();
    return called === owner || (recursive.has(owner) && recursive.has(called));
  }

  /**
   * Pfad für verschachtelte Bäume je Schritt: im Modul-Editor zuerst das Owner-Modul, wie im
   * Design. Einmal je Änderung gebaut, damit die Bäume nicht bei jedem Durchlauf ein neues Array bekommen.
   */
  readonly nestedAncestors = computed(() => {
    const owner = this.moduleId();
    return new Map(
      this.steps().map((step) => [step.id, [owner, step.calledModuleId].filter((id): id is string => !!id)]),
    );
  });

  /** Aufgeklappte MODULE_REF-Zeilen (Step-ID). */
  readonly expandedModuleRows = signal<ReadonlySet<string>>(new Set());
  /** Lazy geladene Modul-Schritte je MODULE_REF-Zeile (Step-ID → Schritte). */
  private readonly moduleStepsCache = signal<Record<string, TestStep[]>>({});
  /** Bekannte Schrittanzahl je MODULE_REF-Zeile (aus Embed oder Lazy-Load); fehlt = noch unbekannt. */
  private readonly moduleStepCounts = signal<Record<string, number>>({});
  /** MODULE_REF-Zeilen, deren Lazy-Load fehlgeschlagen ist (Netz/Rechte) — abgrenzbar von "leer". */
  private readonly moduleLoadErrors = signal<ReadonlySet<string>>(new Set());
  private readonly moduleLoadingIds = signal<ReadonlySet<string>>(new Set());
  private readonly unavailableModuleRows = signal<ReadonlySet<string>>(new Set());
  private readonly embeddedModuleStatuses = signal<Record<string, TestModuleStatus>>({});

  readonly librarySteps = signal<TestStep[]>([]);

  /** Bibliotheks-IDs, von denen eine Kopie am Testfall/Modul hängt (NEX-462 Backref). */
  private readonly activeLibraryStepIds = computed(
    () =>
      new Set(
        this.steps()
          .map((s) => s.libraryStepId)
          .filter((id): id is string => !!id),
      ),
  );

  private readonly editTitleInput = viewChild<ElementRef<HTMLInputElement>>('editTitleInput');
  private readonly editNotesInput = viewChild<ElementRef<HTMLInputElement>>('editNotesInput');
  private readonly editExpectedInput = viewChild<ElementRef<HTMLTextAreaElement>>('editExpectedInput');
  private readonly editTcdlEditor = viewChild(TcdlEditorComponent);
  private focusTcdlOnEdit = false;

  onTcdlAdvance(): void {
    this.editNotesInput()?.nativeElement.focus();
  }

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.kiNeuTimer));
    effect(() => {
      if (this.editingId() !== null) {
        queueMicrotask(() => this.focusEditRow());
      } else {
        this.offeneZeileGemeldet.set(false);
      }
    });
  }

  private focusEditRow(): void {
    const editor = this.focusTcdlOnEdit ? this.editTcdlEditor() : undefined;
    this.focusTcdlOnEdit = false;
    if (editor) {
      editor.focusTextarea();
    } else {
      this.editTitleInput()?.nativeElement.focus();
    }
  }

  ngOnInit(): void {
    this.restoreTcdlAll();
    if (this.bufferedMode()) {
      this.steps.set(this.initialSteps() ?? []);
      this.loading.set(false);
    } else {
      const listCall = this.ownerListCall();
      if (listCall) {
        listCall.subscribe({
          next: (steps) => {
            this.steps.set(steps);
            this.loading.set(false);
          },
          error: () => {
            this.error.set(true);
            this.loading.set(false);
          },
        });
      } else {
        this.loading.set(false);
      }
    }
    // Bibliothek unabhaengig vom Owner laden: der Bibliotheks-Dialog + die Vorschlaege
    // funktionieren auch im Anlegen-Modus (Owner existiert noch nicht), NEX-593.
    this.loadLibrary();
  }

  maintainerName(step: TestStep): string {
    return step.assigneeName || this.testCaseAssigneeName() || '';
  }

  onEditTitleInput(event: Event): void {
    this.editTitle.set(this.inputValue(event));
  }

  onEditActionInput(event: Event): void {
    this.editAction.set(this.inputValue(event));
  }

  onEditExpectedInput(event: Event): void {
    this.editExpected.set(this.inputValue(event));
  }

  onEditNotesInput(event: Event): void {
    this.editNotes.set(this.inputValue(event));
  }

  startDraft(): void {
    if (this.readOnly() || this.busy() || this.rowOpen()) {
      return;
    }
    this.draftStep.set(createDraftStep(this.steps().length));
    this.resetEditState();
    this.editingId.set(DRAFT_ID);
  }

  isDraft(step: TestStep): boolean {
    return isDraftId(step.id);
  }

  openLibrary(): void {
    if (!this.readOnly() && !this.busy()) {
      this.showLibrary.set(true);
    }
  }

  openModuleEmbed(): void {
    if (!this.readOnly() && !this.busy()) {
      this.showModuleEmbed.set(true);
    }
  }

  /** NEX-538: hängt pro gewähltem Modul eine lokale MODULE_REF-Zeile an (dedupe via {@link embeddedModuleIds}). */
  onModuleEmbedApplied(modules: TestModule[]): void {
    if (this.readOnly() || this.busy() || modules.length === 0) {
      return;
    }
    const already = new Set(this.embeddedModuleIds());
    const newRows: TestStep[] = [];
    const counts: Record<string, number> = {};
    const statuses: Record<string, TestModuleStatus> = {};
    for (const module of modules) {
      if (already.has(module.id)) continue;
      already.add(module.id);
      const id = nextLocalId(++this.localIdCounter);
      newRows.push(createLocalModuleRefStep(id, this.steps().length + newRows.length, module.id, module.name));
      if (module.stepCount != null) counts[id] = module.stepCount;
      statuses[id] = module.status;
    }
    if (newRows.length === 0) return;
    this.moduleStepCounts.update((c) => ({ ...c, ...counts }));
    this.embeddedModuleStatuses.update((s) => ({ ...s, ...statuses }));
    this.steps.update((list) => [...list, ...newRows]);
    this.emitChange();
  }

  isModuleRow(step: TestStep): boolean {
    return step.kind === 'MODULE_REF';
  }

  /**
   * Durchgängige hierarchische Nummer eines Kindes einer obersten Modul-Zeile (NEX-826):
   * die Modul-Zeile selbst steht an Position `moduleRowIndex + 1`, ihre Kinder (Schritte UND
   * Untermodule, gemischt) an `{P}.{childIndex + 1}`. Als String, damit `3.1` nicht als Float
   * geparst wird.
   */
  moduleStepNumber(moduleRowIndex: number, childIndex: number): string {
    return `${moduleRowIndex + 1}.${childIndex + 1}`;
  }

  /** NEX-644: Sprung zum eingebundenen Modul — der Parent navigiert (siehe {@link moduleOpen}). */
  openModule(step: TestStep): void {
    if (step.calledModuleId) {
      this.moduleOpen.emit(step.calledModuleId);
    }
  }

  /** NEX-686: Bearbeiten des eingebundenen Moduls im Right-Drawer (siehe {@link editModule}). */
  onEditModule(step: TestStep): void {
    if (!this.readOnly() && this.canEditModule() && step.calledModuleId) {
      this.editModule.emit({ moduleId: step.calledModuleId, stepId: step.id });
    }
  }

  /** NEX-686: Edit-Wunsch aus einem (verschachtelten) Modul-Baum an den Parent weiterreichen. */
  onNestedModuleEdit(moduleId: string): void {
    if (!this.readOnly() && this.canEditModule()) {
      this.editModule.emit({ moduleId, stepId: null });
    }
  }

  private readonly secretColumnError = signal<SecretColumnError | null>(null);

  private readonly secretColumnTargetId = computed(() => {
    const error = this.secretColumnError();
    if (!error) return null;
    const calls = this.steps().filter((s) => s.kind === 'MODULE_REF');
    const ids = [error.testStepId, error.clientId].filter((id): id is string => id !== null);
    const target =
      ids.length > 0
        ? calls.find((s) => ids.includes(s.id) || (s.libraryStepId != null && ids.includes(s.libraryStepId)))
        : calls.find((s) => s.dataSheetId === error.dataSheetId);
    return target?.id ?? null;
  });

  showSecretColumnError(error: SecretColumnError | null): void {
    this.secretColumnError.set(error);
    const target = this.steps().find((s) => s.id === this.secretColumnTargetId());
    if (target && !this.isModuleExpanded(target.id)) this.toggleModuleExpand(target);
  }

  secretColumnErrorFor(step: TestStep): Pick<SecretColumnError, 'field' | 'column'> | null {
    const error = this.secretColumnError();
    return error && this.secretColumnTargetId() === step.id ? { field: error.field, column: error.column } : null;
  }

  isModuleExpanded(stepId: string): boolean {
    return this.expandedModuleRows().has(stepId);
  }

  hasTcdl(step: TestStep): boolean {
    return Boolean(step.tcdl?.trim());
  }

  isTcdlExpanded(stepId: string): boolean {
    return this.expandedTcdlRows().has(stepId);
  }

  toggleTcdl(stepId: string): void {
    this.expandedTcdlRows.update((current) => {
      const next = new Set(current);
      if (!next.delete(stepId)) {
        next.add(stepId);
      }
      return next;
    });
  }

  toggleTcdlAll(): void {
    this.tcdlAll.update((v) => !v);
    this.persistTcdlAll(this.tcdlAll());
  }

  /**
   * Projektweite Persistenz (NEX-1047 Review): wer TCDL sehen will, will es
   * meist ueberall im Projekt sehen. Vermeidet den Wildwuchs pro Testfall und
   * greift auch fuer neu angelegte Owner ohne eigene ID.
   */
  private tcdlAllStorageKey(): string {
    return `to.tcdlAll:${this.projectId()}`;
  }

  private restoreTcdlAll(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(this.tcdlAllStorageKey());
      if (raw === 'true') this.tcdlAll.set(true);
      else if (raw === 'false') this.tcdlAll.set(false);
    } catch {
      /* Storage abgelehnt (Private-Mode, Quota) — im Zweifel Session-only. */
    }
  }

  private persistTcdlAll(value: boolean): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this.tcdlAllStorageKey(), String(value));
    } catch {
      /* Storage abgelehnt — Zustand bleibt im Signal, wird nur nicht gemerkt. */
    }
  }

  /** Passt die Subline dem Owner-Kontext an (Testfall vs. Testmodul). */
  readonly tcdlToggleAllSublineKey = computed<string>(() =>
    this.kontext() === 'modul' || this.moduleId()
      ? 'testSteps.form.tcdlToggleAllSublineModule'
      : 'testSteps.form.tcdlToggleAllSubline',
  );

  textKey(text: 'blockSubtitle' | 'library' | 'tableLabel' | 'emptyTitle'): string {
    if (this.kontext() === 'modul') {
      return `testCases.form.testSteps.modulKontext.${text}`;
    }
    return text === 'emptyTitle' ? 'testCases.form.testSteps.emptyState.title' : `testCases.form.testSteps.${text}`;
  }

  /** Rohzeilen für die Read-Mode-Ansicht (Zeilennummer + M-Marker im Design). */
  tcdlRawLines(step: TestStep): string[] {
    return (step.tcdl ?? '').split('\n');
  }

  tcdlInstructionCount(step: TestStep): number {
    return this.tcdlRawLines(step).filter((l) => l.trim().length > 0).length;
  }

  tcdlManualCount(step: TestStep): number {
    const lines = this.tcdlRawLines(step);
    return lines.filter((l, i) => !l.trim() && i < lines.length - 1).length;
  }

  tcdlIsLong(step: TestStep): boolean {
    return this.tcdlRawLines(step).length > TCDL_COLLAPSE_THRESHOLD;
  }

  tcdlVisibleLines(step: TestStep): string[] {
    const all = this.tcdlRawLines(step);
    if (!this.tcdlIsLong(step) || this.isTcdlExpanded(step.id)) return all;
    return all.slice(0, TCDL_COLLAPSE_THRESHOLD);
  }

  tcdlHiddenLineCount(step: TestStep): number {
    return Math.max(0, this.tcdlRawLines(step).length - TCDL_COLLAPSE_THRESHOLD);
  }

  onEditTcdlChange(value: string): void {
    this.editTcdl.set(value);
  }

  moduleSteps(stepId: string): TestStep[] {
    return this.moduleStepsCache()[stepId] ?? [];
  }

  /** Bekannte Schrittanzahl der Modul-Zeile, sonst null (Badge wird dann ausgeblendet). */
  moduleStepCount(stepId: string): number | null {
    return this.moduleStepCounts()[stepId] ?? null;
  }

  /**
   * NEX-691: Modul-Titel der Zeile, live aus dem {@link TestModuleStore} gespiegelt, sobald
   * das Modul im Drawer gespeichert wurde; sonst der Snapshot-Titel der eingebundenen Zeile.
   */
  liveModuleTitle(step: TestStep): string {
    const live = step.calledModuleId ? this.moduleStore.watch(step.calledModuleId)() : null;
    return live?.name ?? step.title ?? '—';
  }

  /** NEX-691: Schritt-Count der Modul-Zeile, live aus dem Store bevorzugt, sonst der Lazy-Load-Cache. */
  liveModuleStepCount(step: TestStep): number | null {
    const live = this.liveModule(step);
    if (live) {
      if (live.stepCount != null) {
        return live.stepCount;
      }
      if (live.steps) {
        return live.steps.length;
      }
    }
    return this.moduleStepCount(step.id);
  }

  /**
   * NEX-691: Aufgeklappte Modul-Schritte — nach einem Drawer-Save aus dem Live-Store
   * (frischer Stand inkl. verschachtelter Module), sonst der Lazy-Load-Cache. Sorgt dafür,
   * dass Änderungen am Modul sofort in der Testschritt-Tabelle sichtbar sind.
   */
  liveModuleSteps(step: TestStep): TestStep[] {
    const live = this.liveModule(step);
    if (live?.steps) {
      return [...live.steps].sort((a, b) => a.orderIndex - b.orderIndex);
    }
    return this.moduleSteps(step.id);
  }

  /** Store-Detail des eingebundenen Moduls, falls bereits geladen/gespeichert (sonst null). */
  private liveModule(step: TestStep): (TestModule & { steps?: TestStep[] }) | null {
    if (!step.calledModuleId) {
      return null;
    }
    return this.moduleStore.watch(step.calledModuleId)() as (TestModule & { steps?: TestStep[] }) | null;
  }

  /** True, wenn der letzte Lazy-Load dieser Modul-Zeile fehlgeschlagen ist (nicht: leeres Modul). */
  moduleLoadFailed(stepId: string): boolean {
    return this.moduleLoadErrors().has(stepId);
  }

  isModuleLoading(stepId: string): boolean {
    return this.moduleLoadingIds().has(stepId);
  }

  isModuleUnavailable(stepId: string): boolean {
    return this.unavailableModuleRows().has(stepId);
  }

  isModuleDeprecated(step: TestStep): boolean {
    const status = this.liveModule(step)?.status ?? this.embeddedModuleStatuses()[step.id];
    return status === 'DEPRECATED';
  }

  /** Auf-/Zuklappen einer MODULE_REF-Zeile; lädt die Modul-Schritte beim ersten Aufklappen lazy. */
  toggleModuleExpand(step: TestStep): void {
    const willExpand = !this.isModuleExpanded(step.id);
    this.expandedModuleRows.update((current) => {
      const next = new Set(current);
      if (next.has(step.id)) {
        next.delete(step.id);
      } else {
        next.add(step.id);
      }
      return next;
    });
    if (willExpand) this.loadModuleSteps(step);
  }

  /** Erneuter Ladeversuch nach einem fehlgeschlagenen Lazy-Load. */
  retryModuleLoad(step: TestStep): void {
    this.loadModuleSteps(step);
  }

  /** Lädt die Modul-Schritte einmalig; ein Fehler wird markiert (getrennt von "leer") und ist retry-bar. */
  private loadModuleSteps(step: TestStep): void {
    if (!step.calledModuleId || this.moduleStepsCache()[step.id] !== undefined) return;
    this.moduleLoadErrors.update((errors) => withoutId(errors, step.id));
    this.moduleLoadingIds.update((ids) => withId(ids, step.id));
    this.stepService
      .listForModule(this.projectId(), step.calledModuleId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => {
          this.moduleLoadingIds.update((ids) => withoutId(ids, step.id));
          this.moduleStepsCache.update((cache) => ({ ...cache, [step.id]: steps }));
          this.moduleStepCounts.update((c) => ({ ...c, [step.id]: steps.length }));
        },
        error: (err: unknown) => {
          this.moduleLoadingIds.update((ids) => withoutId(ids, step.id));
          if (err instanceof HttpErrorResponse && err.status === 404) {
            this.unavailableModuleRows.update((ids) => withId(ids, step.id));
          } else {
            this.moduleLoadErrors.update((errors) => withId(errors, step.id));
          }
        },
      });
  }

  // ── NEX-1051: TDB-Chips + Override-Block am MODULE_REF-Row ───────────────

  /**
   * Zwischen-Zustand pro Step: die vier Override-Felder aus dem Block. Wird
   * gebraucht, weil einzelne Patches (nur `subj`, nur `ref`) fuer sich keine
   * gueltige VALUE_COMPARE-Bedingung ergeben — das BE akzeptiert nur vollstaendige.
   * So verlieren Zwischenschritte des Nutzers keine Eingaben.
   */
  private readonly overrideDrafts = signal<Record<string, TdbOverride>>({});
  /** Listenmodus: Zeilen je Modulaufruf, solange eine Eingabe noch nicht gespeichert ist. */
  private readonly conditionListDrafts = signal<Record<string, TdbOverride[]>>({});

  readonly isListMode = computed(() => this.conditionMode() === 'list');

  /** Positionen wie die Nummernblase der Zeile, damit der Hinweis am Speichern-Knopf dieselbe Zahl nennt. */
  readonly incompleteConditionStepNumbers = computed<number[]>(() =>
    this.tableRows().flatMap((step, index) => (this.hasMissingCondition(step) ? [index + 1] : [])),
  );

  /** Nur eigene Entwürfe: Eine gespeicherte Bedingung, die das Backend angenommen hat, ist keine Änderung. */
  readonly hasIncompleteConditionDraft = computed(() =>
    this.tableRows().some(
      (step) =>
        this.hasMissingCondition(step) &&
        (this.isListMode() ? !!this.conditionListDrafts()[step.id] : !!this.overrideDrafts()[step.id]),
    ),
  );

  private hasMissingCondition(step: TestStep): boolean {
    if (!this.isModuleRow(step)) return false;
    if (!this.isListMode()) return missingConditionFields(this.currentOverride(step)).length > 0;
    return this.conditionRows(step).some((row) => missingConditionFields(row).length > 0);
  }

  conditionRows(step: TestStep): TdbOverride[] {
    return this.conditionListDrafts()[step.id] ?? fromStepConditions(stepConditionsOf(step)).rows;
  }

  /** Die Führungslinie der Modulschritte wird gestrichelt und rückt ein, sobald „Ausführen wenn“ an ist. */
  conditionsActive(step: TestStep): boolean {
    return this.isListMode() && this.conditionRows(step).length > 0;
  }

  onConditionListChanged(step: TestStep, rows: TdbOverride[]): void {
    if (this.readOnly()) return;
    this.conditionListDrafts.update((drafts) => ({ ...drafts, [step.id]: rows }));
    this.mutateStep(step.id, (s) => {
      const stepConditions = toStepConditions(rows, stepConditionsOf(s));
      return { ...s, stepConditions, stepCondition: stepConditions[0] ?? null };
    });
  }

  /** Objekt- und Fensterauswahl, geladen erst, wenn eine Zeile sie braucht. */
  readonly conditionLibrary = signal<readonly ConditionLibrary[] | null>(null);
  private conditionLibraryRequested = false;
  private readonly loadConditionLibrary = effect(() => {
    if (this.conditionLibraryRequested || !this.isListMode()) return;
    const needed = this.tableRows().some(
      (step) => this.isModuleRow(step) && this.conditionRows(step).some((row) => !!row.subj && row.subj !== 'WERT'),
    );
    if (!needed) return;
    this.conditionLibraryRequested = true;
    // Erst hier geholt: Der Dienst hängt am Router und wird nur gebraucht, wenn eine Zeile ein Objekt prüft.
    this.injector
      .get(ObjectMappingService)
      .conditionLibrary(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (libs) => this.conditionLibrary.set(libs),
        error: () => (this.conditionLibraryRequested = false),
      });
  });

  /** Damit die Modul-Seitenleiste mit der halben Eingabe der Zeile startet statt mit der gespeicherten Bedingung. */
  overrideDraftFor(stepId: string): TdbOverride | null {
    if (!this.isListMode()) return this.overrideDrafts()[stepId] ?? null;
    const rows = this.conditionListDrafts()[stepId];
    if (!rows) return null;
    const step = this.steps().find((s) => s.id === stepId);
    return { ...rows[0], filter: formatFilterExpression(step?.dataSheetFilters) };
  }

  protected currentOverride(step: TestStep): TdbOverride {
    const draft = this.overrideDrafts()[step.id];
    if (draft) return draft;
    return {
      ...fromStepCondition(step.stepCondition ?? null),
      filter: formatFilterExpression(step.dataSheetFilters),
    };
  }

  /** Alle mit dem eingebundenen Modul verknuepften Testdatenblaetter. */
  linkedSheetsForStep(step: TestStep): Testdatenblatt[] {
    const moduleId = step.calledModuleId;
    if (!moduleId) return [];
    return this.availableSheets().filter((s) => s.testModuleIds?.includes(moduleId));
  }

  /**
   * BE-Limit (NEX-1004): pro MODULE_REF-Schritt kann nur EIN Datenblatt einen
   * Override tragen. Chips fuer die restlichen TDBs zeigen die volle Zeilenzahl,
   * der aktive Chip zeigt „eingeschraenkt".
   */
  isChipRestricted(step: TestStep, sheet: Testdatenblatt): boolean {
    if (step.dataSheetId !== sheet.id) return false;
    return this.hasActiveOverride(step);
  }

  /** NEX-2417: „TDB-02 · 5×" mit der Zahl der aktiven Zeilen, eingeschränkt „TDB-02 · eingeschränkt". */
  chipLabel(step: TestStep, sheet: Testdatenblatt): string {
    if (this.isChipRestricted(step, sheet)) {
      return this.translate.instant('tdb.chip.restricted', { key: sheet.key });
    }
    return this.translate.instant('tdb.chip.compact', { key: sheet.key, count: activeRows(sheet) });
  }

  chipTooltip(step: TestStep, sheet: Testdatenblatt): string {
    if (this.isChipRestricted(step, sheet)) {
      const filter = formatFilterExpression(step.dataSheetFilters);
      return this.translate.instant('tdb.chip.tooltipRestricted', {
        key: sheet.key,
        name: sheet.name,
        selection: filter || this.translate.instant('tdb.chip.conditionOnly'),
      });
    }
    return this.translate.instant('tdb.chip.tooltipAll', {
      key: sheet.key,
      name: sheet.name,
      count: activeRows(sheet),
    });
  }

  blockRows(step: TestStep): TestdatenBlockRow[] {
    const linked = this.linkedSheetsForStep(step);
    const override = this.currentOverride(step);
    if (this.isListMode()) {
      // Im Testfall erscheint jedes verbundene Modul-Datenblatt (AK10); eingeschränkt ist nur das aktive (NEX-1004).
      // Die Bedingung steht über dem Modul; Block und Vorschau zeigen nur die Zeilen-Auswahl.
      return linked.map((sheet) => ({
        tdb: toTestdatenblattSummary(sheet),
        override: sheet.id === step.dataSheetId ? { filter: override.filter } : {},
      }));
    }
    const sheet = linked.find((s) => s.id === step.dataSheetId);
    return sheet ? [{ tdb: toTestdatenblattSummary(sheet), override }] : [];
  }

  private activeBlockRow(step: TestStep): TestdatenBlockRow | undefined {
    const rows = this.blockRows(step);
    const active = this.linkedSheetsForStep(step).find((s) => s.id === step.dataSheetId);
    return rows.find((row) => row.tdb.id === active?.key) ?? rows[0];
  }

  moduleTitleFor(step: TestStep): string {
    return this.liveModuleTitle(step);
  }

  /**
   * Der Block sendet Aenderungen als UUID des Datenblatts nicht kennt — er kennt nur
   * den TDB-Key. Wir mappen zurueck auf die interne UUID via {@code activeSheet}.
   */
  onBlockOverrideChanged(step: TestStep, event: { tdbId: string; patch: Partial<TdbOverride> }): void {
    if (this.readOnly()) return;
    const sheet = this.linkedSheetsForStep(step).find((s) => s.key === event.tdbId);
    if (!sheet) return;
    const nextOverride = this.draftOverride(step, event.patch);
    this.mutateStep(step.id, (s) => ({
      ...s,
      dataSheetId: sheet.id,
      dataSheetFilters: filterEntriesFor(nextOverride.filter, s.dataSheetId === sheet.id ? s.dataSheetFilters : null),
      stepCondition: nextStepCondition(s.stepCondition, event.patch, nextOverride),
    }));
  }

  /** NEX-1084: Bedingung eines Modulaufrufs ohne Datenblatt — Datenblatt und Filter bleiben. */
  onConditionChanged(step: TestStep, patch: Partial<TdbOverride>): void {
    if (this.readOnly()) return;
    const nextOverride = this.draftOverride(step, patch);
    this.mutateStep(step.id, (s) => ({ ...s, stepCondition: nextStepCondition(s.stepCondition, patch, nextOverride) }));
  }

  moduleTcdlPreview(step: TestStep): string {
    const override = this.currentOverride(step);
    const sheetRow = this.activeBlockRow(step);
    const idSegment = sheetRow ? tdbIdSegment(sheetRow.tdb.id, override.filter) : '';
    const conditions = this.isListMode()
      ? this.conditionRows(step).flatMap((row) => bedingungFragment(row) ?? [])
      : bedingungFragment(override);
    return formatTcdlPreview(this.moduleTitleFor(step), idSegment, conditions);
  }

  private draftOverride(step: TestStep, patch: Partial<TdbOverride>): TdbOverride {
    const nextOverride: TdbOverride = { ...this.currentOverride(step), ...patch };
    this.overrideDrafts.update((drafts) => ({ ...drafts, [step.id]: nextOverride }));
    return nextOverride;
  }

  onBlockOverrideReset(step: TestStep): void {
    if (this.readOnly()) return;
    this.discardOverrideDraft(step.id);
    // Im Listenmodus steht die Bedingung über dem Modul und gehört nicht zum Testdaten-Block.
    const condition = this.isListMode() ? {} : { stepCondition: null };
    this.mutateStep(step.id, (s) => ({ ...s, dataSheetId: null, dataSheetFilters: null, ...condition }));
  }

  private discardOverrideDraft(stepId: string): void {
    this.overrideDrafts.update((drafts) => {
      const next = { ...drafts };
      delete next[stepId];
      return next;
    });
  }

  private hasActiveOverride(step: TestStep): boolean {
    const hasFilter = (step.dataSheetFilters?.length ?? 0) > 0;
    const hasCondition = this.isListMode()
      ? stepConditionsOf(step).length > 0
      : !!step.stepCondition && Object.keys(step.stepCondition).length > 0;
    return hasFilter || hasCondition;
  }

  /**
   * NEX-1052 Follow-up: Extern getriebene Aktualisierung eines MODULE_REF-Overrides,
   * z. B. wenn der Modul-Drawer eine TDB-Zuordnung im Testfall-Kontext emittiert.
   * Ohne diesen Weg wuerde die interne `steps`-Signalliste nach dem einmaligen
   * `initialSteps`-Seed im `ngOnInit` nicht mehr synchronisiert.
   */
  patchStepOverride(stepId: string, delta: Omit<TdbOverrideDelta, 'stepId'>): void {
    // Die Übernahme aus dem Drawer ist die spätere Eingabe: Sie ersetzt den Zeilen-Entwurf, eine halbe bleibt als Entwurf stehen.
    const override = delta.override;
    if (override) {
      this.overrideDrafts.update((drafts) => ({ ...drafts, [stepId]: override }));
    } else {
      this.discardOverrideDraft(stepId);
    }
    const sheet = { dataSheetId: delta.dataSheetId, dataSheetFilters: delta.dataSheetFilters };
    if (!this.isListMode()) {
      this.mutateStep(stepId, (s) => withFirstStepCondition({ ...s, ...sheet }, delta.stepCondition));
      return;
    }
    const step = this.steps().find((s) => s.id === stepId);
    if (!step) return;
    // Bis der Drawer die Liste kennt, steht er nur für Zeile 1; gespeicherter Index 0 ist das nicht, sobald eine vordere Zeile halb ist.
    const rows = [...drawerConditionRow(override, delta.stepCondition), ...this.conditionRows(step).slice(1)];
    if (rows.some((row) => missingConditionFields(row).length > 0)) {
      this.conditionListDrafts.update((drafts) => ({ ...drafts, [stepId]: rows }));
    } else {
      this.discardConditionListDraft(stepId);
    }
    this.mutateStep(stepId, (s) => {
      const stepConditions = toStepConditions(rows, stepConditionsOf(s));
      // Ohne geladene Liste bleibt es bei `stepCondition`, sonst überdeckte `[]` eine nach dem Speichern aufgerückte Bedingung.
      const list = s.stepConditions || stepConditions.length > 1 ? { stepConditions } : {};
      return { ...s, ...sheet, ...list, stepCondition: stepConditions[0] ?? null };
    });
  }

  private discardConditionListDraft(stepId: string): void {
    this.conditionListDrafts.update((drafts) => {
      const next = { ...drafts };
      delete next[stepId];
      return next;
    });
  }

  /**
   * Nach dem Speichern kann eine verdeckte zweite Bedingung aufgerückt sein (NEX-2254). Die
   * Zeile muss sie zeigen, sonst schickt die nächste Änderung die alte Liste und löscht sie.
   * Ein vollständiger Zeilen-Entwurf ginge der gespeicherten Bedingung vor und fällt deshalb weg;
   * ein unvollständiger bleibt, damit der Hinweis auf die fehlenden Felder stehen bleibt.
   */
  applySavedConditions(saved: readonly TestStep[]): void {
    const savedIds = new Set(saved.map((s) => s.id));
    this.overrideDrafts.update((drafts) =>
      Object.fromEntries(
        Object.entries(drafts).filter(([id, draft]) => !savedIds.has(id) || missingConditionFields(draft).length > 0),
      ),
    );
    this.conditionListDrafts.update((drafts) =>
      Object.fromEntries(
        Object.entries(drafts).filter(
          ([id, rows]) => !savedIds.has(id) || rows.some((row) => missingConditionFields(row).length > 0),
        ),
      ),
    );
    this.steps.update((list) => withSavedStepConditions(list, saved));
  }

  /**
   * Übernimmt einen Vorschlag des KI-Qualitätschecks als ungespeicherte Änderung. Der
   * Schritt-Text ist der Titel, bei Altbestand ohne Titel die Aktion — wie er an die KI ging.
   */
  uebernehmeKiVorschlag(stepId: string, feld: 'ERWARTUNG' | 'SCHRITT', wert: string): boolean {
    if (this.readOnly()) return false;
    const schritt = this.steps().find((s) => s.id === stepId);
    if (!schritt) return false;
    const mitTitel = !!schritt.title?.trim();
    const titel = wert.slice(0, FIELD_LENGTHS.NAME_MAX);
    const ziel = feld === 'ERWARTUNG' ? 'expectedResult' : mitTitel ? 'title' : 'action';
    const neu = ziel === 'title' ? titel : wert;
    this.mutateStep(stepId, (s) => ({ ...s, [ziel]: neu }));
    const bisher = this.kiAenderungen()[stepId] ?? [];
    const frueher = bisher.find((a) => a.ziel === ziel);
    // Eine zweite Übernahme ins selbe Feld behält den Wert vor der ersten, sonst ginge er beim Rückgängig verloren.
    const aenderung: KiAenderung = { feld, ziel, alt: frueher ? frueher.alt : (schritt[ziel] ?? null), neu };
    this.kiAenderungen.update((alle) => ({
      ...alle,
      [stepId]: frueher ? bisher.map((a) => (a === frueher ? aenderung : a)) : [...bisher, aenderung],
    }));
    this.kiHervorheben(stepId);
    this.kiEditorNachziehen(stepId, ziel, neu);
    return true;
  }

  kiBefundUebernehmen(befund: KiSchrittBefund): void {
    if (this.readOnly() || !befund.feld || !befund.vorschlag) return;
    this.kiFokusInZeileHalten(befund.schrittId);
    if (this.uebernehmeKiVorschlag(befund.schrittId, befund.feld, befund.vorschlag)) {
      this.kiUebernehmen.emit(befund.key);
    }
  }

  kiBefundLassen(befund: KiSchrittBefund): void {
    if (this.readOnly()) return;
    this.kiFokusInZeileHalten(befund.schrittId);
    this.kiLassen.emit(befund.key);
  }

  /** Stellt den Wert vor der Übernahme wieder her; der Befund bleibt erledigt. */
  kiRueckgaengig(stepId: string, ziel: KiAenderung['ziel']): void {
    const aenderung = this.kiAenderungen()[stepId]?.find((a) => a.ziel === ziel);
    if (this.readOnly() || !aenderung) return;
    this.mutateStep(stepId, (s) => ({ ...s, [ziel]: aenderung.alt }));
    this.kiAenderungenBehalten(stepId, (a) => a !== aenderung);
    this.kiEditorNachziehen(stepId, ziel, aenderung.alt ?? '');
  }

  /** Markiert in der Zeile die Felder, die ein KI-Vorschlag geändert hat (page-testfaelle.jsx:777). */
  protected kiFeldGeaendert(step: TestStep, ort: 'titel' | 'beschreibung' | 'soll'): boolean {
    return (this.kiAenderungen()[step.id] ?? []).some(({ ziel }) => {
      if (ort === 'soll') return ziel === 'expectedResult';
      if (ort === 'beschreibung') return ziel === 'action' && !!step.title;
      return ziel === 'title' || (ziel === 'action' && !step.title);
    });
  }

  /** Hat die Nutzerin ein übernommenes Feld danach selbst geändert, gilt dessen Diff nicht mehr. */
  private kiAenderungenAbgleichen(stepId: string): void {
    const schritt = this.steps().find((s) => s.id === stepId);
    if (!schritt) return;
    this.kiAenderungenBehalten(stepId, (a) => (schritt[a.ziel] ?? '').trim() === a.neu.trim());
  }

  private kiAenderungenBehalten(stepId: string, behalten: (aenderung: KiAenderung) => boolean): void {
    this.kiAenderungen.update((alle) => {
      const rest = (alle[stepId] ?? []).filter(behalten);
      const andere = Object.fromEntries(Object.entries(alle).filter(([id]) => id !== stepId));
      return rest.length > 0 ? { ...andere, [stepId]: rest } : andere;
    });
  }

  protected kiAktuellerWert(step: TestStep, befund: KiSchrittBefund): string {
    if (befund.feld === 'ERWARTUNG') return step.expectedResult ?? '';
    return step.title?.trim() || step.action || '';
  }

  private kiHervorheben(stepId: string): void {
    clearTimeout(this.kiNeuTimer);
    this.kiNeuId.set(stepId);
    this.kiNeuTimer = setTimeout(() => this.kiNeuId.set(null), KI_HERVORHEBUNG_MS);
  }

  /** Ein offener Inline-Editor hält eigene Werte; ohne Nachziehen überschriebe „Übernehmen“ den Vorschlag still. */
  private kiEditorNachziehen(stepId: string, ziel: KiAenderung['ziel'], wert: string): void {
    if (this.editingId() !== stepId) return;
    if (ziel === 'expectedResult') {
      this.editExpected.set(wert);
      return;
    }
    // Ohne Titel füllt startEdit das Titelfeld mit der Aktion; „Übernehmen“ schriebe sonst die alte Aktion als Titel.
    this.editTitle.set(ziel === 'title' ? wert : wert.slice(0, FIELD_LENGTHS.NAME_MAX));
    if (ziel === 'action') this.editAction.set(wert);
  }

  /** Der Kasten verschwindet mit seinem Knopf; der Fokus bleibt in der Zeile. */
  private kiFokusInZeileHalten(stepId: string): void {
    const zeile = this.host.nativeElement.querySelector<HTMLElement>(`[data-step-id="${CSS.escape(stepId)}"]`);
    if (!zeile) return;
    fokusImBereichHalten(zeile, this.injector, () =>
      zeile
        .querySelector<HTMLElement>(
          '[data-testid="ki-rueckgaengig"], [data-testid="ki-verbessern"], [data-testid="ki-lassen"], .tc-steps__action-btn',
        )
        ?.focus(),
    );
  }

  /** Ohne Umbenennen schickte der nächste Emit die lokalen IDs, und das nächste Speichern legte die Schritte doppelt an. */
  applyClientIdMapping(mapping: Record<string, string>): void {
    if (Object.keys(mapping).length === 0) return;
    const neu = (id: string): string => mapping[id] ?? id;
    const umschluesseln = <T>(record: Record<string, T>): Record<string, T> =>
      Object.fromEntries(Object.entries(record).map(([id, wert]) => [neu(id), wert]));
    const umbenennen = (ids: ReadonlySet<string>): ReadonlySet<string> => new Set([...ids].map(neu));
    this.steps.update((list) => list.map((s) => (mapping[s.id] ? { ...s, id: mapping[s.id] } : s)));
    this.overrideDrafts.update(umschluesseln);
    this.conditionListDrafts.update(umschluesseln);
    this.moduleStepsCache.update(umschluesseln);
    this.moduleStepCounts.update(umschluesseln);
    this.expandedModuleRows.update(umbenennen);
    this.expandedTcdlRows.update(umbenennen);
    this.moduleLoadErrors.update(umbenennen);
    this.kiAenderungen.update(umschluesseln);
    this.editingId.update((id) => (id === null ? null : neu(id)));
  }

  /** Zeigt den ersten Grund, aus dem das Formular nicht speichert; false, wenn die Tabelle keinen hat. */
  fokussiereErstenFehler(): boolean {
    if (this.rowOpen()) {
      this.offeneZeileGemeldet.set(true);
      this.host.nativeElement
        .querySelector('.tc-steps__row--editing')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.focusEditRow();
      return true;
    }
    const schritt = this.tableRows().find((step) => this.hasMissingCondition(step));
    if (!schritt) return false;
    this.expandedModuleRows.update((rows) => new Set(rows).add(schritt.id));
    this.loadModuleSteps(schritt);
    afterNextRender(() => this.fokussiereBedingung(schritt.id), { injector: this.injector });
    return true;
  }

  private fokussiereBedingung(stepId: string): void {
    this.zeigeSchritt(stepId);
    const zeile = this.host.nativeElement.querySelector<HTMLElement>(`[data-step-id="${CSS.escape(stepId)}"]`);
    const ziel =
      zeile?.querySelector<HTMLElement>('[aria-invalid="true"]') ??
      zeile?.querySelector<HTMLElement>('select, input, textarea, button');
    ziel?.focus();
  }

  zeigeSchritt(stepId: string): void {
    const zeile = this.host.nativeElement.querySelector<HTMLElement>(`[data-step-id="${CSS.escape(stepId)}"]`);
    zeile?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  private mutateStep(stepId: string, transform: (s: TestStep) => TestStep): void {
    let found = false;
    this.steps.update((list) =>
      list.map((s) => {
        if (s.id !== stepId) return s;
        found = true;
        return transform(s);
      }),
    );
    if (found) {
      this.emitChange();
    }
  }

  onLibraryApplied(libraryStepIds: string[]): void {
    if (this.readOnly() || this.busy() || libraryStepIds.length === 0) {
      return;
    }
    if (this.isBuffered()) {
      // Anlegen-Modus (NEX-593) oder Buffered-Mode (NEX-599): die Auswahl lokal sammeln.
      this.appendLocalLibraryCopies(libraryStepIds);
      return;
    }
    this.busy.set(true);
    this.error.set(false);
    from(libraryStepIds)
      .pipe(
        concatMap((id) => this.ownerCopyFromLibraryCall(id)!),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        // Jeden kopierten Schritt einzeln anhaengen: bricht ein spaeterer Aufruf ab
        // (z. B. 409/Netzfehler), bleiben die bereits persistierten Schritte sichtbar,
        // statt erst nach einem Reload aufzutauchen (Review NEX-537).
        next: (created) => {
          this.steps.update((list) => [...list, created]);
          this.emitChange();
        },
        error: () => this.failMutation(),
        complete: () => this.busy.set(false),
      });
  }

  /**
   * NEX-546: Baut fuer die eingebundenen Bibliotheks-Schritte Referenz-Zeilen (kein Inhalts-Copy).
   * Jede Zeile traegt libraryStepId = Bibliotheks-ID; der Mapper macht daraus testStepId
   * (Verknuepfung). Inhalt wird nur zur Anzeige aus der Definition uebernommen. Bereits in DIESEM
   * Kontext verknuepfte Definitionen werden uebersprungen (keine Dublette je Verwendung).
   */
  private appendLocalLibraryCopies(libraryStepIds: string[]): void {
    const lib = new Map(this.librarySteps().map((s) => [s.id, s]));
    const alreadyLinked = this.activeLibraryStepIds();
    const newLocals: TestStep[] = [];
    for (const id of libraryStepIds) {
      const src = lib.get(id);
      if (!src || alreadyLinked.has(id)) continue;
      newLocals.push(
        createLocalStep(
          nextLocalId(++this.localIdCounter),
          this.steps().length + newLocals.length,
          src.action ?? '',
          src.expectedResult ?? null,
          src.notes ?? null,
          // NEX-546: verlustfreier Round-Trip — ALLE Inhaltsfelder der Definition uebernehmen,
          // sonst nullt der Link-Save sie auf der geteilten Definition (Marc, #365).
          {
            title: src.title ?? null,
            libraryStepId: src.id,
            kind: src.kind,
            status: src.status,
            calledModuleId: src.calledModuleId ?? null,
            stepRole: src.stepRole,
            category: src.category ?? null,
            assigneeUserId: src.assigneeUserId ?? null,
            assigneeName: src.assigneeName ?? null,
            tags: src.tags ?? [],
          },
        ),
      );
    }
    if (newLocals.length === 0) return;
    this.steps.update((list) => [...list, ...newLocals]);
    this.emitChange();
  }

  private loadLibrary(): void {
    this.stepService
      .listLibrary(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (steps) => this.librarySteps.set(steps),
        error: () => this.librarySteps.set([]),
      });
  }

  startEdit(step: TestStep): void {
    this.requestEdit(step, false);
  }

  startEditTcdl(step: TestStep): void {
    this.requestEdit(step, true);
  }

  confirmSwitch(): void {
    const target = this.pendingSwitch();
    if (!target) {
      return;
    }
    this.pendingSwitch.set(null);
    this.openEditor(target.step, target.tcdl);
  }

  stayInEditor(): void {
    if (!this.pendingSwitch()) {
      return;
    }
    this.pendingSwitch.set(null);
    queueMicrotask(() => this.editTitleInput()?.nativeElement.focus());
  }

  @HostListener('document:keydown.escape')
  onDocumentEscape(): void {
    this.stayInEditor();
  }

  private requestEdit(step: TestStep, tcdl: boolean): void {
    if (this.readOnly() || this.editingId() === step.id) {
      return;
    }
    if (this.openRowChanged()) {
      this.pendingSwitch.set({ step, tcdl });
      return;
    }
    this.openEditor(step, tcdl);
  }

  private openEditor(step: TestStep, tcdl: boolean): void {
    this.draftStep.set(null);
    this.resetEditState();
    this.focusTcdlOnEdit = tcdl;
    this.editingId.set(step.id);
    // Titel und Aktion sind seit NEX-615 getrennte Felder (analog Standalone-Form).
    // Altbestand ohne Titel faellt auf die Aktion zurueck, damit das Pflicht-Titelfeld
    // beim ersten Bearbeiten nicht leer bleibt. Der Vorschlag wird gekuerzt, weil das
    // maxlength des Feldes nur Eingaben begrenzt, nicht Werte, die der Code setzt.
    this.editTitle.set(step.title ?? (step.action ?? '').slice(0, FIELD_LENGTHS.NAME_MAX));
    this.editAction.set(step.action ?? '');
    this.editExpected.set(step.expectedResult ?? '');
    this.editNotes.set(step.notes ?? '');
    this.editTcdl.set(step.tcdl ?? '');
    this.editTcdlOriginal = step.tcdl ?? '';
    this.editFieldsAtOpen.set(this.currentEditFields());
  }

  cancelEdit(): void {
    this.draftStep.set(null);
    this.resetEditState();
  }

  saveEdit(): void {
    const id = this.editingId();
    if (this.readOnly() || !id || this.busy()) {
      return;
    }
    if (!this.canSaveEdit()) {
      this.markMissingRequiredFields();
      return;
    }
    const title = this.editTitle().trim();
    const action = this.editAction().trim();
    if (isDraftId(id)) {
      this.persistDraft(title, action);
      return;
    }
    if (isLocalId(id) || this.isBuffered()) {
      this.updateLocalStep(id, title, action);
      return;
    }
    this.persistUpdate(id, title, action);
  }

  private persistUpdate(id: string, title: string, action: string): void {
    // Titel und Aktion werden seit NEX-615 getrennt gepatcht; frueher trug bei
    // Bibliotheks-Kopien die Aktion faelschlich den Titel.
    const request: UpdateTestStepRequest = {
      title,
      action,
      expectedResult: this.editExpected().trim() || null,
      notes: this.editNotes().trim() || null,
      ...(this.editTcdl() === this.editTcdlOriginal ? {} : { tcdl: this.editTcdl().trim() ? this.editTcdl() : '' }),
    };
    const call = this.ownerUpdateCall(id, request);
    if (!call) {
      return;
    }
    this.busy.set(true);
    this.error.set(false);
    call.subscribe({
      next: (updated) => {
        this.steps.update((list) => list.map((s) => (s.id === updated.id ? updated : s)));
        this.kiAenderungenAbgleichen(updated.id);
        this.cancelEdit();
        this.busy.set(false);
        this.emitChange();
      },
      error: () => this.failMutation(),
    });
  }

  delete(step: TestStep): void {
    if (this.readOnly()) {
      return;
    }
    if (!confirm(this.translate.instant('testCases.form.testSteps.deleteConfirm'))) {
      return;
    }
    this.removeStep(step);
  }

  moveUp(index: number): void {
    if (this.readOnly()) {
      return;
    }
    if (index > 0) {
      this.swap(index, index - 1);
    }
  }

  moveDown(index: number): void {
    if (this.readOnly()) {
      return;
    }
    if (index < this.steps().length - 1) {
      this.swap(index, index + 1);
    }
  }

  private persistDraft(title: string, action: string): void {
    const request = this.buildCreateRequest(title, action);
    const call = this.ownerCreateCall(request);
    if (!call) {
      this.appendStep(
        createLocalStep(
          nextLocalId(++this.localIdCounter),
          this.steps().length,
          action,
          request.expectedResult ?? null,
          request.notes ?? null,
          { title, tcdl: request.tcdl ?? null },
        ),
      );
      return;
    }
    this.busy.set(true);
    this.error.set(false);
    call.subscribe({
      next: (created) => {
        this.appendStep(created);
        this.busy.set(false);
      },
      error: () => this.failMutation(),
    });
  }

  private appendStep(step: TestStep): void {
    this.steps.update((list) => [...list, step]);
    this.draftStep.set(null);
    this.resetEditState();
    this.emitChange();
  }

  private buildCreateRequest(title: string, action: string): CreateTestStepRequest {
    return {
      kind: 'ACTION',
      title,
      action,
      expectedResult: this.editExpected().trim() || null,
      notes: this.editNotes().trim() || null,
      tcdl: this.editTcdl().trim() ? this.editTcdl() : null,
    };
  }

  private markMissingRequiredFields(): void {
    this.editAttempted.set(true);
    if (!this.requiredMissing()) {
      return;
    }
    const first = this.titleMissing() ? this.editTitleInput() : this.editExpectedInput();
    queueMicrotask(() => first?.nativeElement.focus());
  }

  private resetEditState(): void {
    this.editAttempted.set(false);
    this.editingId.set(null);
    this.editTitle.set('');
    this.editAction.set('');
    this.editExpected.set('');
    this.editNotes.set('');
    this.editTcdl.set('');
    this.editTcdlOriginal = '';
    this.editFieldsAtOpen.set(this.currentEditFields());
  }

  private currentEditFields(): Record<'title' | 'action' | 'expected' | 'notes' | 'tcdl', string> {
    return {
      title: this.editTitle(),
      action: this.editAction(),
      expected: this.editExpected(),
      notes: this.editNotes(),
      tcdl: this.editTcdl(),
    };
  }

  private removeStep(step: TestStep): void {
    if (this.busy()) {
      return;
    }
    if (isLocalId(step.id) || this.isBuffered()) {
      this.steps.update((list) => list.filter((s) => s.id !== step.id));
      this.emitChange();
      return;
    }
    const call = this.ownerDeleteCall(step.id);
    if (!call) {
      return;
    }
    this.busy.set(true);
    this.error.set(false);
    call.subscribe({
      next: () => {
        this.steps.update((list) => list.filter((s) => s.id !== step.id));
        this.busy.set(false);
        this.emitChange();
      },
      error: () => this.failMutation(),
    });
  }

  private swap(from: number, to: number): void {
    if (this.busy()) {
      return;
    }
    const previous = this.steps();
    const reordered = [...previous];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    if (this.isBuffered()) {
      this.steps.set(reordered);
      this.emitChange();
      return;
    }
    this.persistOrder(reordered, previous);
  }

  private persistOrder(reordered: TestStep[], previous: TestStep[]): void {
    const call = this.ownerReorderCall(reordered.map((s) => s.id));
    if (!call) {
      return;
    }
    this.steps.set(reordered);
    this.emitChange();
    this.busy.set(true);
    this.error.set(false);
    call.subscribe({
      next: (result) => {
        this.steps.set(result);
        this.busy.set(false);
      },
      error: () => {
        this.steps.set(previous);
        this.failMutation();
      },
    });
  }

  private updateLocalStep(id: string, title: string, action: string): void {
    const expectedResult = this.editExpected().trim() || null;
    const notes = this.editNotes().trim() || null;
    // NEX-615: Titel und Aktion sind getrennte Felder; die Aktion traegt nicht mehr
    // faelschlich den Titel.
    // NEX-546: Der fruehere NEX-548-Detach (Kappen von libraryStepId beim Edit) ist
    // zurueckgebaut. Die Definition einer verknuepften Zeile ist geteilt — Editieren behaelt
    // die Referenz, damit die Aenderung ueberall wirkt.
    const tcdl = this.editTcdl().trim() ? this.editTcdl() : null;
    this.steps.update((list) =>
      list.map((s) => (s.id === id ? { ...s, title, action, expectedResult, notes, tcdl } : s)),
    );
    this.kiAenderungenAbgleichen(id);
    this.cancelEdit();
    this.emitChange();
  }

  flushPendingSteps(newTestCaseId: string): Observable<FlushResult> {
    return this.flushPendingToTarget(
      (req) => this.stepService.create(this.projectId(), newTestCaseId, req),
      (libId) => this.stepService.copyFromLibrary(this.projectId(), newTestCaseId, libId),
    );
  }

  /** Modul-Variante (NEX-593): persistiert lokal gesammelte Schritte nach dem Modul-Create. */
  flushPendingStepsForModule(newModuleId: string): Observable<FlushResult> {
    return this.flushPendingToTarget(
      (req) => this.stepService.createForModule(this.projectId(), newModuleId, req),
      (libId) => this.stepService.copyFromLibraryToModule(this.projectId(), newModuleId, libId),
    );
  }

  private flushPendingToTarget(
    create: (req: CreateTestStepRequest) => Observable<TestStep>,
    copyLibrary: (libraryStepId: string) => Observable<TestStep>,
  ): Observable<FlushResult> {
    const pending = this.steps().filter((s) => isLocalId(s.id));
    if (pending.length === 0) {
      return of({ persisted: [], failed: [] });
    }
    return from(pending).pipe(
      concatMap((s) => {
        // NEX-546: Verknuepfte Zeilen (libraryStepId gesetzt) werden ueber copyFromLibrary
        // persistiert — auch wenn inline editiert. Das BE verknuepft die geteilte Definition und
        // wendet die mitgelieferten Inhalte darauf an (kein Detach mehr, ex-NEX-548). Nur echte,
        // neu getippte Schritte laufen ueber create().
        const call$ = s.libraryStepId
          ? copyLibrary(s.libraryStepId)
          : create({
              kind: 'ACTION',
              title: s.title ?? null,
              action: s.action ?? '',
              expectedResult: s.expectedResult ?? null,
              notes: s.notes ?? null,
              tcdl: s.tcdl ?? null,
            });
        return call$.pipe(catchError(() => of({ failed: s })));
      }),
      toArray(),
      concatMap((items) =>
        of({
          persisted: items.filter(isPersisted),
          failed: items.filter(isFailed).map((f) => f.failed),
        }),
      ),
    );
  }

  private failMutation(): void {
    this.error.set(true);
    this.busy.set(false);
  }

  /** Combined dirty signal (NEX-535) + steps snapshot (NEX-599) for the parent form. */
  private emitChange(): void {
    this.changed.emit();
    this.stepsChanged.emit(this.steps());
  }

  private ownerListCall(): Observable<TestStep[]> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.list(this.projectId(), tcId);
    const mId = this.moduleId();
    if (mId) return this.stepService.listForModule(this.projectId(), mId);
    return null;
  }

  private ownerCreateCall(request: CreateTestStepRequest): Observable<TestStep> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.create(this.projectId(), tcId, request);
    const mId = this.moduleId();
    if (mId) return this.stepService.createForModule(this.projectId(), mId, request);
    return null;
  }

  private ownerUpdateCall(stepId: string, request: UpdateTestStepRequest): Observable<TestStep> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.update(this.projectId(), tcId, stepId, request);
    const mId = this.moduleId();
    if (mId) return this.stepService.updateForModule(this.projectId(), mId, stepId, request);
    return null;
  }

  private ownerDeleteCall(stepId: string): Observable<void> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.delete(this.projectId(), tcId, stepId);
    const mId = this.moduleId();
    if (mId) return this.stepService.deleteForModule(this.projectId(), mId, stepId);
    return null;
  }

  private ownerReorderCall(orderedIds: string[]): Observable<TestStep[]> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.reorder(this.projectId(), tcId, orderedIds);
    const mId = this.moduleId();
    if (mId) return this.stepService.reorderForModule(this.projectId(), mId, orderedIds);
    return null;
  }

  private ownerCopyFromLibraryCall(libraryStepId: string): Observable<TestStep> | null {
    if (this.bufferedMode()) return null;
    const tcId = this.testCaseId();
    if (tcId) return this.stepService.copyFromLibrary(this.projectId(), tcId, libraryStepId);
    const mId = this.moduleId();
    if (mId) return this.stepService.copyFromLibraryToModule(this.projectId(), mId, libraryStepId);
    return null;
  }

  private inputValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }
}

function activeRows(sheet: Testdatenblatt): number {
  return activeRowCount(sheet.zeilen.length, sheet.zeilenAktiv);
}
